import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Calendar, ClipboardCheck, MessageSquare, Target, Pencil, Trash2, Copy, UserRound, GitMerge } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOKR, useUpdateOKR, useUpdateProgress, useAddKeyResult, useUpdateKeyResult, useDeleteKeyResult, useDuplicateKeyResult, useCloseOKR, useSubmitForApproval, usePermissions } from '../../hooks';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { useTeams, useTeamMembers } from '../../hooks/useTeams';
import { SubmitForApprovalButton, CommentSection, KRCommentSection, CheckInForm, CheckInHistory, CheckInScheduleSettings, SelectOKRModal, ChildOKRSection } from '../../components/okr';
import type { KeyResult } from '../../types/okr';
import { toast } from 'react-hot-toast';
import { getErrorMessage } from '../../services/api';
import { AICoachDrawer } from '../../components/ai/AICoachDrawer';
import { useAICoachDrawerOpen } from '../../hooks/useAICoachDrawerOpen';
import { keyResultProgressPercent } from '../../utils/progress';
import { okrStatusLabel } from '../../utils/okrStatus';
import { useGoBack, useOpenWithReturn } from '../../hooks/useReturnNavigation';

type TabType = 'details' | 'checkin' | 'comments' | 'aligned';

const VALID_TABS: TabType[] = ['details', 'checkin', 'comments', 'aligned'];

export const OKRDetails: React.FC = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // "← Back" returns to the view that opened this OKR, filters included (utils/returnTo.ts).
  // Reached some other way (bookmark, pasted link), it goes to the OKR list.
  const goBack = useGoBack('/okrs');
  const openWithReturn = useOpenWithReturn();
  const { user } = useAuth();
  const { data: okr, isLoading, error, refetch } = useOKR(id!);
  const updateProgressMutation = useUpdateProgress();
  const addKeyResultMutation = useAddKeyResult();
  const updateKeyResultMutation = useUpdateKeyResult();
  const deleteKeyResultMutation = useDeleteKeyResult();
  const duplicateKeyResultMutation = useDuplicateKeyResult();
  const closeOKRMutation = useCloseOKR();
  const submitForApprovalMutation = useSubmitForApproval();
  const [aiCoachOpen] = useAICoachDrawerOpen();

  // Permission hook must be called before any early returns
  const { can } = usePermissions();

  // Team OKR: fetch teams list and team members for KR owner assignment
  const isTeamOKR = okr?.type === 'team';
  const { data: allTeams = [] } = useTeams();
  const { data: teamMembers = [] } = useTeamMembers(okr?.teamId || '');
  const updateOKRMutation = useUpdateOKR();

  // Build assignable users from team members
  const assignableUsers = teamMembers.map(m => ({
    id: m.userId,
    label: `${m.firstName} ${m.lastName} (${m.department})`
  }));

  // Handle associating a team with this OKR
  const handleTeamChange = async (newTeamId: string) => {
    if (!okr) return;
    try {
      await updateOKRMutation.mutateAsync({
        id: okr.id,
        data: { teamId: newTeamId || null }
      });
      refetch();
    } catch (error) {
      console.error('Failed to update team:', error);
    }
  };

  // Tab state from URL
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(tabFromUrl || 'details');

  // Update URL when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // Carry the state along, or switching tabs here would forget where "← Back" goes.
    setSearchParams(tab === 'details' ? {} : { tab }, { state: location.state });
  };

  // Sync tab from URL when the `?tab=` query param changes for the SAME OKR (e.g.
  // clicking a tab, or browser back/forward through tab history). Does NOT fire on
  // initial mount's id resolution — that's handled by the id-change effect below, which
  // must win when both the id and the tab query change together (see its comment).
  useEffect(() => {
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Reset tab when the route's OKR id changes. `/okrs/:id` is a single route with no
  // `key`, so navigating from one OKR to another (e.g. clicking a child row in the
  // Aligned tab) does NOT remount OKRDetails — nothing else resets `activeTab`, so the
  // previous OKR's tab selection (e.g. Aligned) would otherwise stay selected against the
  // new OKR's data, even rendering that OKR's empty Aligned state instead of its Key
  // Results. Honour an explicit `?tab=` on the new URL (e.g. a bookmarked link); default
  // to 'details' otherwise.
  const previousIdRef = useRef(id);
  useEffect(() => {
    if (previousIdRef.current !== id) {
      previousIdRef.current = id;
      setActiveTab(tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'details');
    }
  }, [id, tabFromUrl]);

  const [editingKeyResult, setEditingKeyResult] = useState<string | null>(null);
  const [progressValue, setProgressValue] = useState('');
  const [progressNotes, setProgressNotes] = useState('');

  // Add Key Result state
  const [isAddingKR, setIsAddingKR] = useState(false);
  const [newKR, setNewKR] = useState({
    title: '',
    description: '',
    unitType: 'percentage' as 'percentage' | 'number' | 'currency' | 'boolean',
    startValue: 0,
    targetValue: 100,
    unit: '%',
    ownerId: ''
  });

  // Edit Key Result state
  const [editingKRDetails, setEditingKRDetails] = useState<string | null>(null);
  const [editKRForm, setEditKRForm] = useState({
    title: '',
    description: '',
    startValue: 0,
    targetValue: 100,
    unit: '%',
    ownerId: '' as string | undefined
  });

  // Copy Key Result state
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [keyResultToCopy, setKeyResultToCopy] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-cream">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !okr) {
    return (
      <div className="flex items-center justify-center h-screen bg-cream">
        <Card className="p-8 text-center">
          <p className="text-danger-600 mb-4">Failed to load OKR</p>
          <Button variant="secondary" onClick={goBack}>
            Back to OKRs
          </Button>
        </Card>
      </div>
    );
  }

  const handleUpdateProgress = async (keyResult: KeyResult) => {
    try {
      await updateProgressMutation.mutateAsync({
        keyResultId: keyResult.id,
        data: {
          value: parseFloat(progressValue),
          notes: progressNotes
        }
      });

      setEditingKeyResult(null);
      setProgressValue('');
      setProgressNotes('');
      refetch();
    } catch (error) {
      console.error('Update progress error:', error);
    }
  };

  const handleAddKeyResult = async () => {
    if (!newKR.title.trim()) {
      return;
    }

    try {
      await addKeyResultMutation.mutateAsync({
        okrId: id!,
        data: {
          title: newKR.title,
          description: newKR.description,
          unitType: newKR.unitType,
          startValue: Number(newKR.startValue),
          targetValue: Number(newKR.targetValue),
          unit: newKR.unit,
          ...(isTeamOKR && newKR.ownerId && { ownerId: newKR.ownerId })
        }
      });

      // Reset form
      setIsAddingKR(false);
      setNewKR({
        title: '',
        description: '',
        unitType: 'percentage',
        startValue: 0,
        targetValue: 100,
        unit: '%',
        ownerId: ''
      });

      // Auto-submit for approval if OKR is already active/approved
      if (okr && okr.status === 'active') {
        try {
          await submitForApprovalMutation.mutateAsync(id!);
          toast.success('New Key Result added. OKR has been resubmitted for approval.');
        } catch (approvalError) {
          console.error('Auto-submit for approval error:', approvalError);
          // Don't fail the whole operation if approval submission fails
          toast('Key Result added successfully. Please manually submit for approval.', { icon: 'ℹ️' });
        }
      }

      refetch();
    } catch (error) {
      console.error('Add key result error:', error);
    }
  };

  const handleEditKRClick = (kr: KeyResult) => {
    setEditingKRDetails(kr.id);
    setEditKRForm({
      title: kr.title,
      description: kr.description || '',
      startValue: kr.startValue,
      targetValue: kr.targetValue,
      unit: kr.unit || '%',
      ownerId: kr.ownerId || ''
    });
  };

  const handleUpdateKeyResult = async () => {
    if (!editKRForm.title.trim() || !editingKRDetails) {
      return;
    }

    try {
      await updateKeyResultMutation.mutateAsync({
        keyResultId: editingKRDetails,
        data: {
          title: editKRForm.title,
          description: editKRForm.description,
          startValue: Number(editKRForm.startValue),
          targetValue: Number(editKRForm.targetValue),
          unit: editKRForm.unit,
          ...(isTeamOKR && { ownerId: editKRForm.ownerId || null })
        }
      });

      setEditingKRDetails(null);
      refetch();
    } catch (error) {
      console.error('Update key result error:', error);
    }
  };

  const handleDeleteKeyResult = async (keyResultId: string) => {
    if (!confirm('Are you sure you want to delete this key result?')) {
      return;
    }

    try {
      await deleteKeyResultMutation.mutateAsync(keyResultId);
      refetch();
      toast.success('Key Result deleted successfully');
    } catch (error) {
      console.error('Delete key result error:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleCopyKeyResult = (keyResultId: string) => {
    setKeyResultToCopy(keyResultId);
    setIsCopyModalOpen(true);
  };

  const handleSelectTargetOKR = async (targetOkrId: string) => {
    if (!keyResultToCopy) return;

    try {
      await duplicateKeyResultMutation.mutateAsync({
        keyResultId: keyResultToCopy,
        targetOkrId
      });

      setIsCopyModalOpen(false);
      setKeyResultToCopy(null);
      refetch();
      toast.success('Key Result copied successfully');
    } catch (error) {
      console.error('Copy key result error:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'secondary'> = {
      on_track: 'success',
      at_risk: 'warning',
      off_track: 'danger',
      completed: 'success',
      draft: 'secondary',
      closed: 'secondary', // S1-g
      deleted: 'secondary'
    };
    // Was `status.replace('_', ' ')`, which rendered lowercase and only ever replaced the
    // FIRST underscore. okrStatusLabel gives the same casing as every other status badge.
    return <Badge variant={variants[status] || 'secondary'}>{okrStatusLabel(status)}</Badge>;
  };

  const getApprovalStatusBadge = (approvalStatus?: string) => {
    if (!approvalStatus) return null;

    const variants: Record<string, 'success' | 'warning' | 'danger'> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger'
    };
    return <Badge variant={variants[approvalStatus] || 'warning'}>{approvalStatus}</Badge>;
  };

  // Check if current user is the owner
  const isOwner = user?.id === okr?.ownerId;
  const isAccountAdmin = user?.role === 'account_admin';
  const isManager = user?.id === okr?.owner?.managerId;

  // Permission-based access (combines role permission with ownership)
  const hasOKRUpdatePermission = can('okr', 'update');
  const hasKRCreatePermission = can('key_result', 'create');
  const hasKRUpdatePermission = can('key_result', 'update');
  const hasKRDeletePermission = can('key_result', 'delete');

  // Allow editing if user has permission AND is owner/admin/manager, AND OKR is not in final state
  const canEdit = hasOKRUpdatePermission && (isOwner || isAccountAdmin || isManager) && !['completed', 'cancelled', 'closed', 'deleted'].includes(okr.status);
  const canAddKeyResult = hasKRCreatePermission && (isOwner || isAccountAdmin || isManager) && !['completed', 'cancelled', 'closed', 'deleted'].includes(okr.status);
  const canUpdateProgress = hasKRUpdatePermission;
  const canDeleteKeyResult = hasKRDeletePermission && (isAccountAdmin || isManager);

  return (
    <div
      className="min-h-screen bg-cream transition-[padding] duration-300 ease-in-out"
      style={{ paddingRight: aiCoachOpen ? 300 : 0 }}
    >
      {/* Header - Warm gradient */}
      <div className="bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
        <div className="px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center">
              <Button variant="ghost" onClick={goBack} className="mr-4 text-white hover:bg-white/10">
                ← Back
              </Button>
              <div>
                <h1 className="text-3xl font-semibold text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{okr.title}</h1>
                <p className="text-sm text-stone-300 mt-1">
                  {okr.type.charAt(0).toUpperCase() + okr.type.slice(1)} OKR •
                  Owner: {okr.owner.firstName} {okr.owner.lastName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {getStatusBadge(okr.status)}
              {getApprovalStatusBadge(okr.approvalStatus)}
              <Button
                variant="outline"
                onClick={() => openWithReturn(`/okrs/${okr.id}/approval-history`)}
                className="border-white/30 text-white hover:bg-white/10"
              >
                Approval History
              </Button>
              {canEdit && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => openWithReturn(`/okrs/${okr.id}/edit`)}
                    className="bg-white text-stone-800 hover:bg-stone-100"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (window.confirm(`Close "${okr.title}"? This OKR will be closed and can no longer be edited.`)) {
                        closeOKRMutation.mutate(okr.id, {
                          onSuccess: () => {
                            toast.success('OKR closed');
                            refetch();
                          },
                          onError: (err: any) => {
                            toast.error(err?.response?.data?.message || err?.message || 'Failed to close OKR');
                          },
                        });
                      }
                    }}
                    className="bg-white/10 text-white border-white/30 hover:bg-white/20"
                    disabled={closeOKRMutation.isPending}
                  >
                    Close
                  </Button>
                </>
              )}
              <SubmitForApprovalButton
                okrId={okr.id}
                currentStatus={okr.status}
                isOwner={isOwner}
                onSuccess={refetch}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        {/* OKR Details - Two column layout on large screens */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Main Objective Card */}
          <Card className="p-6 lg:col-span-2">
            <h2 className="text-xl font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Objective</h2>
            <p className="text-stone-600 text-lg">{okr.description || 'No description provided'}</p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-sand rounded-xl p-4">
                <p className="text-sm text-stone-500 mb-1">Owner</p>
                <p className="font-medium text-stone-900">
                  {okr.owner.firstName} {okr.owner.lastName}
                </p>
                {okr.owner.department && (
                  <p className="text-sm text-stone-500">{okr.owner.department}</p>
                )}
              </div>
              {(okr.startDate || okr.endDate) && (
                <div className="bg-sand rounded-xl p-4">
                  <p className="text-sm text-stone-500 mb-1">Date Range</p>
                  <p className="font-medium text-stone-900">
                    {okr.startDate ? new Date(okr.startDate).toLocaleDateString() : 'Not set'} - {okr.endDate ? new Date(okr.endDate).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Progress & Cycle Sidebar */}
          <div className="space-y-6">
            {/* Overall Progress Card */}
            <Card className="p-6">
              <p className="text-sm text-stone-500 mb-3">Overall Progress</p>
              <div className="flex items-center gap-4 mb-3">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 transform -rotate-90">
                    <circle
                      className="text-stone-100"
                      strokeWidth="8"
                      stroke="currentColor"
                      fill="transparent"
                      r="32"
                      cx="40"
                      cy="40"
                    />
                    <circle
                      className={`${
                        (okr.progress || 0) >= 70 ? 'text-success-500' :
                        (okr.progress || 0) >= 40 ? 'text-warning-500' : 'text-primary-500'
                      }`}
                      strokeWidth="8"
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="32"
                      cx="40"
                      cy="40"
                      strokeDasharray={`${2 * Math.PI * 32}`}
                      strokeDashoffset={`${2 * Math.PI * 32 * (1 - (okr.progress || 0) / 100)}`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold text-stone-900">{okr.progress || 0}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{okr.keyResults.length}</p>
                  <p className="text-sm text-stone-500">Key Results</p>
                </div>
              </div>
            </Card>

            {/* Cycle Card */}
            {okr.cycle && (
              <Card className="p-6 bg-primary-50 border-primary-100">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-primary-500" />
                  <p className="text-sm font-medium text-primary-700">Active Cycle</p>
                </div>
                <p className="font-semibold text-stone-900 text-lg">{okr.cycle.name}</p>
                <span className="inline-block text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded mt-1 mb-2">
                  {okr.cycle.type}
                </span>
                <p className="text-sm text-stone-600">
                  {new Date(okr.cycle.startDate).toLocaleDateString()} - {new Date(okr.cycle.endDate).toLocaleDateString()}
                </p>
              </Card>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-stone-200 mb-6">
          <nav className="flex gap-6">
            {[
              { id: 'details' as TabType, label: 'Key Results', icon: Target },
              { id: 'checkin' as TabType, label: 'Check-ins', icon: ClipboardCheck },
              { id: 'comments' as TabType, label: 'Comments', icon: MessageSquare },
              {
                id: 'aligned' as TabType,
                label: `Aligned (${okr.childOKRs?.length ?? 0})`,
                icon: GitMerge,
              }
            ].map(({ id: tabId, label, icon: Icon }) => (
              <button
                key={tabId}
                onClick={() => handleTabChange(tabId)}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 transition-colors font-medium text-sm ${
                  activeTab === tabId
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <>
            {/* Key Results */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Key Results ({okr.keyResults.length})</h2>
            {canAddKeyResult && !isAddingKR && (
              <Button
                variant="secondary"
                onClick={() => setIsAddingKR(true)}
              >
                + Add Key Result
              </Button>
            )}
          </div>

              {/* Team selector for team OKRs — allows associating/changing the team */}
              {isTeamOKR && canEdit && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Select
                    label="Team"
                    value={okr.teamId || ''}
                    onChange={(e) => handleTeamChange(e.target.value)}
                    options={[
                      { value: '', label: 'Select a team to assign Key Results to members', disabled: false },
                      ...allTeams.map(t => ({
                        value: t.id,
                        label: `${t.name}${t.description ? ` — ${t.description}` : ''} (${t.memberCount} members)`,
                        disabled: false
                      }))
                    ]}
                    disabled={updateOKRMutation.isPending}
                  />
                </div>
              )}

          {/* Add Key Result Form */}
          {isAddingKR && (
            <div className="mb-4 border border-stone-200 rounded-xl p-4 bg-sand">
              <h3 className="font-semibold text-stone-900 mb-3">Add New Key Result</h3>
              <div className="space-y-3">
                <Input
                  label="Title"
                  value={newKR.title}
                  onChange={(e) => setNewKR({ ...newKR, title: e.target.value })}
                  placeholder="e.g., Achieve NPS score of 50"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Description (Optional)</label>
                  <textarea
                    value={newKR.description}
                    onChange={(e) => setNewKR({ ...newKR, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                    placeholder="Additional details about this key result"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="Start Value"
                    type="number"
                    value={newKR.startValue}
                    onChange={(e) => setNewKR({ ...newKR, startValue: parseFloat(e.target.value) || 0 })}
                  />
                  <Input
                    label="Target Value"
                    type="number"
                    value={newKR.targetValue}
                    onChange={(e) => setNewKR({ ...newKR, targetValue: parseFloat(e.target.value) || 0 })}
                  />
                  <Input
                    label="Unit"
                    value={newKR.unit}
                    onChange={(e) => setNewKR({ ...newKR, unit: e.target.value })}
                    placeholder="%"
                  />
                </div>

                {/* Assign to team member - only for team OKRs */}
                {isTeamOKR && assignableUsers.length > 0 && (
                  <Select
                    label="Assign to"
                    value={newKR.ownerId}
                    onChange={(e) => setNewKR({ ...newKR, ownerId: e.target.value })}
                    options={[
                      { value: '', label: 'Unassigned', disabled: false },
                      ...assignableUsers.map(u => ({
                        value: u.id,
                        label: u.label,
                        disabled: false
                      }))
                    ]}
                  />
                )}

                <div className="flex gap-3 justify-end pt-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsAddingKR(false);
                      setNewKR({
                        title: '',
                        description: '',
                        unitType: 'percentage',
                        startValue: 0,
                        targetValue: 100,
                        unit: '%',
                        ownerId: ''
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAddKeyResult}
                    disabled={!newKR.title.trim() || addKeyResultMutation.isPending}
                  >
                    {addKeyResultMutation.isPending ? (
                      <span className="flex items-center">
                        <LoadingSpinner size="sm" className="mr-2" />
                        Adding...
                      </span>
                    ) : (
                      'Add Key Result'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {okr.keyResults.map((kr) => {
              const progress = keyResultProgressPercent(kr);
              const isEditingProgress = editingKeyResult === kr.id;
              const isEditingDetails = editingKRDetails === kr.id;

              return (
                <div
                  key={kr.id}
                  className={`border border-stone-200 rounded-xl p-4 transition-all ${
                    !isEditingDetails && !isEditingProgress && canUpdateProgress
                      ? 'hover:shadow-soft-sm hover:border-primary-300 hover:bg-primary-50/30 cursor-pointer'
                      : 'hover:shadow-soft-sm'
                  }`}
                  onClick={() => {
                    // Only trigger progress update when clicking the card itself (not when editing or when buttons are clicked)
                    if (!isEditingProgress && !isEditingDetails && canUpdateProgress) {
                      setEditingKeyResult(kr.id);
                      setProgressValue(kr.currentValue.toString());
                    }
                  }}
                >
                  {/* Edit KR Details Form */}
                  {isEditingDetails ? (
                    <div className="bg-sand p-4 rounded-lg">
                      <h4 className="font-semibold text-stone-900 mb-3">Edit Key Result</h4>
                      <div className="space-y-3">
                        <Input
                          label="Title"
                          value={editKRForm.title}
                          onChange={(e) => setEditKRForm({ ...editKRForm, title: e.target.value })}
                          required
                        />

                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Description</label>
                          <textarea
                            value={editKRForm.description}
                            onChange={(e) => setEditKRForm({ ...editKRForm, description: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <Input
                            label="Start Value"
                            type="number"
                            value={editKRForm.startValue}
                            onChange={(e) => setEditKRForm({ ...editKRForm, startValue: parseFloat(e.target.value) || 0 })}
                          />
                          <Input
                            label="Target Value"
                            type="number"
                            value={editKRForm.targetValue}
                            onChange={(e) => setEditKRForm({ ...editKRForm, targetValue: parseFloat(e.target.value) || 0 })}
                          />
                          <Input
                            label="Unit"
                            value={editKRForm.unit}
                            onChange={(e) => setEditKRForm({ ...editKRForm, unit: e.target.value })}
                          />
                        </div>

                        {/* Assign to team member - only for team OKRs */}
                        {isTeamOKR && assignableUsers.length > 0 && (
                          <Select
                            label="Assign to"
                            value={editKRForm.ownerId || ''}
                            onChange={(e) => setEditKRForm({ ...editKRForm, ownerId: e.target.value })}
                            options={[
                              { value: '', label: 'Unassigned', disabled: false },
                              ...assignableUsers.map(u => ({
                                value: u.id,
                                label: u.label,
                                disabled: false
                              }))
                            ]}
                          />
                        )}

                        <div className="flex gap-3 justify-end pt-2">
                          <Button
                            variant="secondary"
                            onClick={() => setEditingKRDetails(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            onClick={handleUpdateKeyResult}
                            disabled={!editKRForm.title.trim() || updateKeyResultMutation.isPending}
                          >
                            {updateKeyResultMutation.isPending ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-stone-900 mb-1">{kr.title}</h3>
                          {kr.description && (
                            <p className="text-sm text-stone-500 mb-2">{kr.description}</p>
                          )}
                          {/*
                            Backlog B1. Who owns the work, on the row that describes the work.
                            Rendered unconditionally, including the unowned case: an absent line
                            would be indistinguishable from an owner the page failed to load, and
                            "nobody owns this key result" is the more useful of the two readings.
                            "Unassigned" is this page's own word for it — the owner dropdowns
                            below (lines ~602, ~720) already use it.
                          */}
                          <p className="text-sm text-stone-500 flex items-center gap-1.5">
                            <UserRound className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                            <span>
                              {kr.owner
                                ? `${kr.owner.firstName} ${kr.owner.lastName}`
                                : 'Unassigned'}
                            </span>
                          </p>
                        </div>

                        <div className="flex gap-2">
                          {canEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click from triggering
                                handleEditKRClick(kr);
                              }}
                              className="p-2 text-stone-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Edit Key Result"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {/* Show copy button if user can create key results */}
                          {canAddKeyResult && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click from triggering
                                handleCopyKeyResult(kr.id);
                              }}
                              className="p-2 text-stone-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Copy Key Result to Another OKR"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          )}
                          {/* Only show delete if user has permission AND is admin/manager */}
                          {canDeleteKeyResult && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click from triggering
                                handleDeleteKeyResult(kr.id);
                              }}
                              disabled={deleteKeyResultMutation.isPending}
                              className="p-2 text-stone-600 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete Key Result"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Progress Display */}
                  {!isEditingProgress && !isEditingDetails && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-stone-500">
                          Current: {kr.currentValue} {kr.unit} of {kr.targetValue} {kr.unit}
                        </span>
                        <span className="text-sm font-semibold text-stone-900">{progress}%</span>
                      </div>
                      <div className="w-full bg-stone-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            progress >= 70 ? 'bg-success-500' :
                            progress >= 40 ? 'bg-warning-500' : 'bg-danger-500'
                          }`}
                          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Progress Update Form */}
                  {isEditingProgress && (
                    <div className="bg-sand p-4 rounded-lg mt-3">
                      <h4 className="font-semibold text-stone-900 mb-3">Update Progress</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-stone-700 mb-1">
                              Current Value ({kr.unit})
                            </label>
                            <div className="px-3 py-2 bg-stone-100 border border-stone-200 rounded-lg text-stone-600">
                              {kr.currentValue}
                            </div>
                          </div>
                          <Input
                            label={`New Value (${kr.unit})`}
                            type="number"
                            value={progressValue}
                            onChange={(e) => setProgressValue(e.target.value)}
                            step="0.1"
                            min={kr.startValue}
                            max={kr.targetValue}
                          />
                          <div>
                            <label className="block text-sm font-medium text-stone-700 mb-1">
                              Target Value ({kr.unit})
                            </label>
                            <div className="px-3 py-2 bg-stone-100 border border-stone-200 rounded-lg text-stone-600">
                              {kr.targetValue}
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">
                            Notes (optional)
                          </label>
                          <textarea
                            value={progressNotes}
                            onChange={(e) => setProgressNotes(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                            placeholder="Add notes about this update..."
                          />
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setEditingKeyResult(null);
                              setProgressValue('');
                              setProgressNotes('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleUpdateProgress(kr)}
                            disabled={updateProgressMutation.isPending}
                          >
                            {updateProgressMutation.isPending ? 'Saving...' : 'Save Progress'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recent Updates - Show even when editing to provide context */}
                  {kr.progressUpdates && kr.progressUpdates.length > 0 && !isEditingDetails && (
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <p className="text-sm font-medium text-stone-700 mb-2">Recent Updates:</p>
                      <div className="space-y-1">
                        {kr.progressUpdates.slice(0, 3).map((update) => (
                          <div key={update.id} className="text-sm text-stone-500">
                            {new Date(update.createdAt).toLocaleDateString()} -
                            Updated to {update.newValue} by {update.updatedBy?.firstName}
                            {update.notes && `: "${update.notes}"`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Result Comments - Inline expandable section */}
                  {!isEditingDetails && !isEditingProgress && (
                    <KRCommentSection keyResultId={kr.id} />
                  )}
                </div>
              );
            })}
          </div>
        </Card>
          </>
        )}

        {/* Check-in Tab Content */}
        {activeTab === 'checkin' && (
          <div className="space-y-6">
            <CheckInScheduleSettings okr={okr} canConfigure={can('check_in', 'configure')} />
            {/* Only show CheckInForm for active OKRs (not draft, completed, cancelled, etc.) */}
            {can('check_in', 'create') && isOwner &&
             ['active', 'on_track', 'at_risk', 'off_track'].includes(okr.status) &&
             okr.checkInFrequency && okr.checkInFrequency !== 'none' && (
              <CheckInForm okr={okr} onSuccess={() => refetch()} />
            )}
            <CheckInHistory okrId={okr.id} />
          </div>
        )}

        {/* Comments Tab Content */}
        {activeTab === 'comments' && (
          <Card className="p-6">
            <CommentSection okrId={id!} />
          </Card>
        )}

        {/* Aligned Tab Content — the OKRs that roll up into this objective. The data is
            already on the wire from the detail query; this tab is its first web consumer. */}
        {activeTab === 'aligned' && (
          <Card className="p-6">
            <ChildOKRSection
              childOKRs={okr.childOKRs}
              onSelect={(id) => openWithReturn(`/okrs/${id}`)}
            />
          </Card>
        )}

      </div>

      {/* Copy Key Result Modal */}
      <SelectOKRModal
        isOpen={isCopyModalOpen}
        onClose={() => {
          setIsCopyModalOpen(false);
          setKeyResultToCopy(null);
        }}
        onSelect={handleSelectTargetOKR}
        isLoading={duplicateKeyResultMutation.isPending}
      />

      {/* AI Coach drawer — collapsible right rail */}
      <AICoachDrawer okrId={okr.id} />
    </div>
  );
};
